<template>
  <Transition name="gp-fade">
    <div
      v-if="current"
      class="gp-overlay"
      role="dialog"
      aria-modal="true"
      :aria-label="current.title"
      @click.self="dismiss"
    >
      <div class="gp-modal" :class="`gp-modal--${current.type}`">

        <header class="gp-modal__header">
          <h2 class="gp-modal__title">{{ current.title }}</h2>
          <button type="button" class="gp-modal__close" aria-label="Kapat" @click="dismiss">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </header>

        <div class="gp-modal__body">
          <div v-if="safeHeroImageUrl" class="gp-modal__poster">
            <img :src="safeHeroImageUrl" :alt="current.title" class="gp-modal__poster-img">
          </div>

          <div
            v-if="current.content"
            class="gp-modal__content"
            v-html="safeContent"
          ></div>
        </div>

        <footer class="gp-modal__footer">
          <a
            v-if="current.buttonText && safeButtonUrl !== '#'"
            :href="safeButtonUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="gp-modal__btn-primary"
          >
            {{ current.buttonText }}
          </a>
          <button type="button" class="gp-modal__btn-secondary" @click="dismiss">
            Tamam
          </button>
        </footer>

      </div>
    </div>
  </Transition>
</template>

<script>
import { defineComponent, ref, onMounted, computed } from 'vue'
import axios from 'axios'
import { sanitizeHtml } from '@/utils/sanitizeHtml'

const ALLOWED_PROTOCOLS = ['http:', 'https:', 'mailto:']

function sanitizeUrl(url) {
  try {
    const u = new URL(url, location.origin)
    return ALLOWED_PROTOCOLS.includes(u.protocol) ? u.href : '#'
  } catch {
    return '#'
  }
}

const DISMISSED_KEY = 'dismissed_popups'

function getDismissed() {
  try { return JSON.parse(sessionStorage.getItem(DISMISSED_KEY) || '[]') } catch { return [] }
}

function markDismissed(id) {
  const list = getDismissed()
  if (!list.includes(id)) {
    list.push(id)
    sessionStorage.setItem(DISMISSED_KEY, JSON.stringify(list))
  }
}

export default defineComponent({
  name: 'GlobalPopup',
  setup() {
    const queue = ref([])
    const current = ref(null)

    function showNext() {
      current.value = queue.value.length > 0 ? queue.value[0] : null
    }

    function dismiss() {
      if (current.value) {
        markDismissed(current.value.id)
        queue.value.shift()
        showNext()
      }
    }

    onMounted(async () => {
      try {
        const res = await axios.get('/popups/active', { skipGlobalLoader: true, withCredentials: true })
        const dismissed = getDismissed()
        queue.value = (Array.isArray(res.data) ? res.data : []).filter(p => !dismissed.includes(p.id))
        showNext()
      } catch {
        // Popup yüklenemezse uygulamayı engellemez
      }
    })

    const safeContent = computed(() => sanitizeHtml(current.value?.content || ''))
    const safeButtonUrl = computed(() => sanitizeUrl(current.value?.buttonUrl || ''))
    const safeHeroImageUrl = computed(() => {
      const url = current.value?.heroImageUrl ? sanitizeUrl(current.value.heroImageUrl) : '#'
      return /^https?:/i.test(url) ? url : ''
    })

    return { current, dismiss, safeContent, safeButtonUrl, safeHeroImageUrl }
  }
})
</script>

<style>
/* ---- Overlay ---- */
.gp-overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  padding-top: calc(20px + var(--app-safe-top));
  padding-bottom: calc(20px + var(--app-safe-bottom));
  background: rgba(17, 24, 39, 0.55);
}

/* ---- Modal ---- */
.gp-modal {
  width: min(640px, 100%);
  max-height: min(780px, calc(100dvh - 40px - var(--app-safe-top) - var(--app-safe-bottom)));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 28px;
  background: #fff;
  border-top: 4px solid var(--gp-accent, #3b82f6);
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.18);
}

.gp-modal--info    { --gp-accent: #3b82f6; }
.gp-modal--warning { --gp-accent: #f59e0b; }
.gp-modal--success { --gp-accent: #22c55e; }
.gp-modal--error   { --gp-accent: #ef4444; }

/* ---- Header ---- */
.gp-modal__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  padding: 22px 24px 16px;
  border-bottom: 1px solid #edf0f4;
  flex-shrink: 0;
}

.gp-modal__title {
  margin: 0;
  color: #111827;
  font-size: 20px;
  font-weight: 600;
  line-height: 1.25;
}

.gp-modal__close {
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 0;
  border-radius: 50%;
  background: #f3f4f6;
  color: #6b7280;
  cursor: pointer;
  transition: background 0.15s;
}
.gp-modal__close:hover { background: #e5e7eb; color: #111827; }

/* ---- Body ---- */
.gp-modal__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 20px 24px;
}

.gp-modal__poster {
  position: relative;
  aspect-ratio: 16 / 7;
  overflow: hidden;
  border-radius: 18px;
  margin-bottom: 18px;
  background: #f6f7f9;
}

.gp-modal__poster-img {
  width: 100%;
  height: 100%;
  display: block;
  object-fit: cover;
}

.gp-modal__content {
  color: #374151;
  font-size: 15px;
  line-height: 1.65;
}

.gp-modal__content p  { margin: 0 0 1em; }
.gp-modal__content p:last-child { margin-bottom: 0; }
.gp-modal__content ul, .gp-modal__content ol { padding-left: 1.4em; margin: 0 0 1em; }
.gp-modal__content a  { color: var(--gp-accent, #3b82f6); }

/* ---- Footer ---- */
.gp-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px 20px;
  border-top: 1px solid #edf0f4;
  flex-shrink: 0;
}

.gp-modal__btn-primary,
.gp-modal__btn-secondary {
  min-height: 44px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 0;
  border-radius: 999px;
  padding: 0 22px;
  font-size: 14px;
  font-weight: 500;
  line-height: 1;
  text-decoration: none;
  cursor: pointer;
  transition: opacity 0.15s;
}

.gp-modal__btn-primary {
  background: var(--gp-accent, #3b82f6);
  color: #fff;
}
.gp-modal__btn-primary:hover { opacity: 0.88; }

.gp-modal__btn-secondary {
  background: #f3f4f6;
  color: #374151;
}
.gp-modal__btn-secondary:hover { background: #e5e7eb; }

/* ---- Transition ---- */
.gp-fade-enter-active,
.gp-fade-leave-active {
  transition: opacity 0.22s ease;
}
.gp-fade-enter-from,
.gp-fade-leave-to {
  opacity: 0;
}

/* ---- Mobile ---- */
@media (max-width: 600px) {
  .gp-modal {
    width: calc(100% - 32px);
    max-height: calc(100dvh - 64px - var(--app-safe-top) - var(--app-safe-bottom));
    border-radius: 22px;
  }

  .gp-modal__header,
  .gp-modal__body,
  .gp-modal__footer {
    padding-left: 18px;
    padding-right: 18px;
  }

  .gp-modal__title { font-size: 17px; }
}
</style>
